import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
    BaseComponentProps,
    createComponentBase,
} from "../../../libs/components";
import { Tooltip } from "../tooltip";

import {
    Alignment,
    ClassicEditor,
    AccessibilityHelp,
    Autosave,
    Bold,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    ListProperties,
    Paragraph,
    SelectAll,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TodoList,
    Undo,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

export interface richTextEditorProps extends BaseComponentProps {
    label?: string;
    value: string;
    onChanged: (event: string) => void;
    placeholder?: string;
    error?: string;
    isError?: boolean;
}

export const RichTextEditor = ({
    label,
    value,
    onChanged,
    placeholder = "Type or paste your content here!",
    error,
    isError,
    className,
}: richTextEditorProps) => {
    const editorContainerRef = React.useRef(null);
    const editorRef = React.useRef(null);
    const componentClassName = "a-richTextEditor";
    const modifiers = {
        "error": isError,
    };
    const { Container } = createComponentBase(componentClassName, "div");

    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const [data, setData] = useState(value);

    useEffect(() => {
        setData(value);
    }, [value]);

    useEffect(() => {
        setIsLayoutReady(true);

        return () => setIsLayoutReady(false);
    }, []);

    const editorConfig = {
        toolbar: {
            items: [
                "undo",
                "redo",
                "|",
                "selectAll",
                "|",
                "fontSize",
                "fontFamily",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "bold",
                "italic",
                "alignment",
                "|",
                "insertTable",
                "|",
                "link",
                "|",
                "bulletedList",
                "numberedList",
                "todoList",
                "outdent",
                "indent",
                "|",
            ],
            shouldNotGroupWhenFull: false,
        },
        plugins: [
            Alignment,
            AccessibilityHelp,
            Autosave,
            Bold,
            Essentials,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            Indent,
            IndentBlock,
            Italic,
            Link,
            List,
            ListProperties,
            Paragraph,
            SelectAll,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableProperties,
            TableToolbar,
            TodoList,
            Undo,
        ],
        fontFamily: {
            supportAllValues: true,
        },
        fontSize: {
            options: [10, 12, 14, 16, 18, 20, 22, 30],
            supportAllValues: true,
        },
        list: {
            properties: {
                styles: true,
                startIndex: true,
                reversed: true,
            },
            supportAllValues: true,
        },
        placeholder: placeholder,
        table: {
            contentToolbar: [
                "tableColumn",
                "tableRow",
                "mergeTableCells",
                "tableProperties",
                "tableCellProperties",
            ],
        },
    };

    return (
        <Container className={className} modifiers={modifiers}>
            {label && (
                <label className={`${componentClassName}__label`}>{label}</label>
            )}
            <Tooltip message={error} isVisible={isError}>
                <div className={`${componentClassName}__editor`}>
                    <div className="main-container">
                        <div
                            className="editor-container editor-container_classic-editor editor-container_include-style"
                            ref={editorContainerRef}
                        >
                            <div className="editor-container__editor">
                                <div ref={editorRef}>
                                    {isLayoutReady && (
                                        <CKEditor
                                            editor={ClassicEditor}
                                            config={editorConfig}
                                            data={data}
                                            onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setData(data);
                                                onChanged(data);
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Tooltip>
        </Container>
    );
};
